import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowLeft, Lightning, Flame, FloppyDisk, Pencil, ArrowsClockwise,
  MapPin, ChartLine, Receipt, FileText, Paperclip, ChatTeardropText,
  ActivityIcon as Activity, Drop, Cube, ListChecks,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PodDetailPage() {
  const { leadId, podIndex } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/pods/${leadId}/${podIndex}`,
                               { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setEditForm(d.pod_data || {});
      } else if (res.status === 409) {
        const d = await res.json();
        setError(d.detail);
      } else {
        toast.error('POD non trovato');
        navigate(-1);
      }
    } catch (e) { toast.error('Errore'); }
    finally { setLoading(false); }
  }, [leadId, podIndex, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/pods/${leadId}/${podIndex}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        toast.success('POD aggiornato');
        setIsEditing(false);
        fetchData();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Errore');
      }
    } catch (e) { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento POD...</div>;
  if (error) return (
    <div className="p-8 max-w-2xl mx-auto">
      <Button variant="ghost" onClick={() => navigate(-1)}>
        <ArrowLeft size={18} className="mr-1" /> Indietro
      </Button>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-4">
        <p className="text-yellow-700">{error}</p>
      </div>
    </div>
  );
  if (!data) return null;

  const pod = data.pod_data || {};
  const isGas = !!pod.pdr_code;
  const Icon = isGas ? Flame : Lightning;
  const code = isGas ? pod.pdr_code : pod.pod_code;

  const updateField = (k, v) => setEditForm({ ...editForm, [k]: v });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="pod-detail-page">
      {/* HEADER */}
      <div className="flex items-center gap-3 mb-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/customers/${leadId}`)}>
          <ArrowLeft size={18} className="mr-1" /> {data.ragione_sociale}
        </Button>
        <div className="text-slate-300">/</div>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2 flex-1">
          <Icon size={22} weight="duotone" className={isGas ? 'text-blue-500' : 'text-yellow-500'} />
          <span className="font-mono">{code || 'POD'}</span>
          {pod.fornitore_attuale && <Badge variant="outline">{pod.fornitore_attuale}</Badge>}
          {pod.tensione && <Badge variant="outline">{pod.tensione}</Badge>}
        </h1>
      </div>

      {/* TOOLBAR */}
      <div className="flex flex-wrap gap-2 mb-5 sticky top-0 bg-slate-50/95 backdrop-blur z-10 py-2 border-b">
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)} className="bg-orange-500 hover:bg-orange-600"
                  data-testid="edit-pod-btn">
            <Pencil size={14} className="mr-1" /> Modifica
          </Button>
        ) : (
          <>
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600"
                    data-testid="save-pod-btn">
              <FloppyDisk size={14} className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
            <Button variant="outline" onClick={() => { setIsEditing(false); setEditForm(pod); }}>
              Annulla
            </Button>
          </>
        )}
        <Button variant="outline" disabled className="border-purple-200 text-purple-600 opacity-50"
                title="In sviluppo">Voltura</Button>
        <Button variant="outline" disabled className="border-purple-200 text-purple-600 opacity-50"
                title="In sviluppo">Cambia offerta</Button>
        <Button variant="outline" className="border-blue-200 text-blue-600"
                onClick={() => toast.info('Confronto con registro SII (RCU) in costruzione')}>
          <ListChecks size={14} className="mr-1" /> RCU
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={['tecnici', 'dettagli', 'fatture']} className="space-y-2">

        {/* DATI POD TECNICI */}
        <Section value="tecnici" title="Dati POD tecnici" icon={Cube}>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Field label={isGas ? 'Codice PDR' : 'Codice POD'} value={code} mono editing={isEditing}
                   onChange={(v) => updateField(isGas ? 'pdr_code' : 'pod_code', v)} />
            <Field label="Distributore" value={editForm.distributore} editing={isEditing}
                   onChange={(v) => updateField('distributore', v)} />
            <Field label="Fornitore attuale" value={editForm.fornitore_attuale} editing={isEditing}
                   onChange={(v) => updateField('fornitore_attuale', v)} />
            {!isGas && (
              <>
                <Field label="Tensione" value={editForm.tensione} editing={isEditing}
                       onChange={(v) => updateField('tensione', v)} />
                <Field label="Tensione (V)" value={editForm.tensione_volt} editing={isEditing}
                       onChange={(v) => updateField('tensione_volt', v)} />
                <Field label="Potenza impegnata (kW)" value={editForm.potenza_impegnata} editing={isEditing}
                       onChange={(v) => updateField('potenza_impegnata', v)} />
                <Field label="Potenza disponibile (kW)" value={editForm.potenza_disponibile} editing={isEditing}
                       onChange={(v) => updateField('potenza_disponibile', v)} />
              </>
            )}
            <Field label="Tipologia uso" value={editForm.tipologia_uso} editing={isEditing}
                   onChange={(v) => updateField('tipologia_uso', v)} />
            <Field label="Profilo prelievo" value={editForm.profilo_prelievo} editing={isEditing}
                   onChange={(v) => updateField('profilo_prelievo', v)} />
            <Field label="Codice cliente competitor" value={editForm.codice_cliente_competitor} mono editing={isEditing}
                   onChange={(v) => updateField('codice_cliente_competitor', v)} />
          </div>
          <div className="mt-3 pt-3 border-t flex gap-4 text-xs text-slate-500">
            <span>Telegestito: <strong className={pod.telegestito ? 'text-green-600' : 'text-red-600'}>
              {pod.telegestito ? 'SI' : 'NO'}</strong></span>
            <span>Disalimentabile: <strong className={pod.disalimentabile ? 'text-green-600' : 'text-red-600'}>
              {pod.disalimentabile ? 'SI' : 'NO'}</strong></span>
          </div>
        </Section>

        {/* INDIRIZZO */}
        <Section value="indirizzo" title="Indirizzo fornitura" icon={MapPin}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Indirizzo" value={editForm.indirizzo_fornitura} editing={isEditing}
                   onChange={(v) => updateField('indirizzo_fornitura', v)} />
            <Field label="Comune" value={editForm.comune_fornitura} editing={isEditing}
                   onChange={(v) => updateField('comune_fornitura', v)} />
            <Field label="Provincia" value={editForm.provincia_fornitura} editing={isEditing}
                   onChange={(v) => updateField('provincia_fornitura', v)} />
            <Field label="CAP" value={editForm.cap_fornitura} editing={isEditing}
                   onChange={(v) => updateField('cap_fornitura', v)} />
          </div>
        </Section>

        {/* DETTAGLI COMMERCIALI */}
        <Section value="dettagli" title="Dettagli commerciali" icon={Activity}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-slate-500">Cliente</div>
              <div className="text-sm font-medium">
                <a href={`/customers/${leadId}`} className="text-orange-600 hover:underline">
                  {data.ragione_sociale}
                </a>
                <span className="text-slate-400 ml-2 font-mono text-xs">{data.partita_iva}</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Contratti associati</div>
              <div className="text-sm">{data.contracts.length}</div>
            </div>
          </div>
          {(data.contracts || []).length > 0 && (
            <div className="mt-3 pt-3 border-t space-y-1">
              {data.contracts.map(c => (
                <div key={c.id} className="flex items-center justify-between text-sm border rounded p-2">
                  <div>
                    <span className="font-medium">{c.offer_name || 'Contratto'}</span>
                    <span className="text-slate-400 ml-2 text-xs">{(c.created_at || '').slice(0, 10)}</span>
                  </div>
                  <Badge className={c.status === 'attivo' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                    {c.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* CONSUMI ANNUI */}
        {!isGas && (
          <Section value="consumi" title="Consumi annui (da fattura)" icon={ChartLine}>
            <div className="grid grid-cols-4 gap-3 text-center">
              <ConsumiCard label="Totale" value={pod.consumo_annuo} unit="kWh/anno" color="bg-orange-50 text-orange-700" />
              <ConsumiCard label="F1" value={pod.consumo_F1_anno} unit="kWh" color="bg-yellow-50 text-yellow-700" />
              <ConsumiCard label="F2" value={pod.consumo_F2_anno} unit="kWh" color="bg-amber-50 text-amber-700" />
              <ConsumiCard label="F3" value={pod.consumo_F3_anno} unit="kWh" color="bg-red-50 text-red-700" />
            </div>
            <p className="text-xs text-slate-400 italic mt-3">
              Consumi orari, mensilità reali e letture distributore: in costruzione (richiede integrazione SII).
            </p>
          </Section>
        )}

        {/* FATTURE */}
        <Section value="fatture" title={`Fatture analizzate (${data.fatture.length})`} icon={Receipt}>
          {data.fatture.length === 0 ? (
            <p className="text-sm text-slate-400">Nessuna fattura analizzata per questo POD</p>
          ) : (
            <div className="space-y-1">
              {data.fatture.map(f => (
                <div key={f.id} className="flex items-center justify-between text-sm border rounded p-2 hover:bg-slate-50">
                  <div>
                    <span className="font-medium">{f.fornitore_attuale}</span>
                    {f.consumi_anno && <span className="text-slate-500 text-xs ml-2">{Number(f.consumi_anno).toLocaleString('it-IT')} kWh</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    {f.totale_eur && <span className="font-mono text-xs">{f.totale_eur} €</span>}
                    <span className="text-slate-400 text-xs">{(f.data || '').slice(0, 10)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* PLACEHOLDER */}
        <Section value="letture" title="Letture distributore" icon={Drop}>
          <p className="text-sm text-slate-400 italic">Modulo letture distributore in sviluppo (richiede sync SII).</p>
        </Section>
        <Section value="tickets" title="Tickets / Pratiche" icon={ChatTeardropText}>
          <p className="text-sm text-slate-400 italic">Modulo ticketing in sviluppo.</p>
        </Section>
        <Section value="allegati" title="Allegati POD" icon={Paperclip}>
          <p className="text-sm text-slate-400 italic">Allegati specifici del POD (in sviluppo).</p>
        </Section>
        <Section value="attivita" title="Attività & Log" icon={Activity}>
          <p className="text-xs text-slate-500">
            Ultimo aggiornamento: {(pod.updated_at || pod.created_at || '').slice(0, 19).replace('T', ' ') || '—'}
          </p>
        </Section>
      </Accordion>
    </div>
  );
}

function Section({ value, title, icon: Icon, children }) {
  return (
    <AccordionItem value={value} className="border rounded-lg bg-white px-4">
      <AccordionTrigger className="hover:no-underline" data-testid={`section-${value}`}>
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} weight="duotone" className="text-orange-500" />}
          <span className="font-semibold text-slate-900">{title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-2 pb-4">{children}</AccordionContent>
    </AccordionItem>
  );
}

function Field({ label, value, mono, editing, onChange }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      {editing && onChange ? (
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
               className={`mt-0.5 ${mono ? 'font-mono' : ''}`} />
      ) : (
        <div className={`text-sm mt-0.5 ${mono ? 'font-mono' : ''} ${value ? 'text-slate-900' : 'text-slate-400 italic'}`}>
          {value || '—'}
        </div>
      )}
    </div>
  );
}

function ConsumiCard({ label, value, unit, color }) {
  return (
    <div className={`rounded p-3 ${color}`}>
      <div className="text-xs">{label}</div>
      <div className="text-xl font-bold">{value ? Number(value).toLocaleString('it-IT') : '—'}</div>
      <div className="text-[10px] opacity-75">{unit}</div>
    </div>
  );
}
